import { useEffect, useMemo, useState } from "react";
import { Button, Checkbox } from "@humansignal/ui";
import { Modal } from "../../components/Modal/Modal";
import { cn } from "../../utils/bem";

/**
 * "Choose fields" — grouped checklists of every path the export can
 * write, with search and per-group select-all. The dataset groups are
 * on by default; "Platform bookkeeping" (skip flags, comment counters,
 * dispute timestamps, internal ids…) is off by default.
 *
 * `fields` is null for the dataset default, or an explicit array of
 * dotted paths (the picker always emits an explicit list once touched).
 */

const GROUPS = [
  { key: "task", label: "Task", prefix: "", hint: "Task id, data, metadata and timestamps.", defaultOn: true },
  {
    key: "data",
    label: "Data columns",
    prefix: "data.",
    hint: "The keys of each task's data — e.g. text, image, audio, csv — as imported.",
    defaultOn: true,
  },
  {
    key: "annotation",
    label: "Annotation",
    prefix: "annotations.",
    hint: "Who annotated, when, review outcome, timing.",
    defaultOn: true,
  },
  {
    key: "result",
    label: "Result entries",
    prefix: "annotations.result.",
    hint: "Extra keys on each result — type, value, from_name and to_name are always written.",
    defaultOn: true,
  },
  {
    key: "bookkeeping",
    label: "Platform bookkeeping",
    prefix: "",
    hint: "Skip flags, comment counters, dispute timestamps, internal ids. Not dataset content.",
    defaultOn: false,
  },
];

const block = cn("fields-modal");

export const FieldsModal = ({ visible, inventory, fields, onApply, onClose }) => {
  const groups = useMemo(
    () =>
      GROUPS.map((g) => ({
        ...g,
        items: (inventory?.[g.key] ?? []).map((k) => ({ key: k, path: `${g.prefix}${k}` })),
      })),
    [inventory],
  );
  const allPaths = useMemo(() => groups.flatMap((g) => g.items.map((i) => i.path)), [groups]);
  const defaultPaths = useMemo(
    () => groups.filter((g) => g.defaultOn).flatMap((g) => g.items.map((i) => i.path)),
    [groups],
  );
  const [draft, setDraft] = useState(
    () => new Set(fields ? fields.filter((p) => p !== "annotations.result") : defaultPaths),
  );
  const [query, setQuery] = useState("");
  // Phones: full-screen sheet with one scroll and a sticky footer.
  const [phone, setPhone] = useState(
    () => typeof window !== "undefined" && !!window.matchMedia?.("(max-width: 700px)")?.matches,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 700px)");
    const onChange = () => setPhone(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  const q = query.trim().toLowerCase();
  const matches = (k) => !q || k.toLowerCase().includes(q);
  const toggle = (path) =>
    setDraft((d) => {
      const n = new Set(d);
      n.has(path) ? n.delete(path) : n.add(path);
      return n;
    });
  const setGroup = (g, on) =>
    setDraft((d) => {
      const n = new Set(d);
      g.items.forEach((i) => (on ? n.add(i.path) : n.delete(i.path)));
      return n;
    });
  const sameAs = (list) => draft.size === list.length && list.every((p) => draft.has(p));
  const isDefault = sameAs(defaultPaths);

  const apply = () => {
    onApply(isDefault ? null : ["annotations.result", ...allPaths.filter((p) => draft.has(p))]);
    onClose();
  };

  if (!visible) return null;
  return (
    <Modal
      visible
      onHide={onClose}
      title="Choose fields"
      style={phone ? undefined : { width: "min(940px, 92vw)" }}
      closeOnClickOutside={false}
      footer={renderFooter()}
      bareFooter
    >
      <div className={block.mod({ phone }).toClassName()}>
        <div className={block.elem("top").toClassName()}>
          <input
            className={`${cn("input-ls").toClassName()} ${block.elem("search").toClassName()}`}
            placeholder="Filter fields…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Filter fields"
          />
        </div>

        <div className={block.elem("groups").toClassName()}>
          {groups.map((g) => {
            if (!g.items.length) return null;
            const visibleItems = g.items.filter((i) => matches(i.key));
            const onCount = g.items.filter((i) => draft.has(i.path)).length;
            return (
              <section key={g.key} className={block.elem("group").mod({ muted: !g.defaultOn }).toClassName()}>
                <header className={block.elem("group-head").toClassName()}>
                  <Checkbox
                    checked={onCount === g.items.length}
                    indeterminate={onCount > 0 && onCount < g.items.length}
                    onChange={(e) => setGroup(g, !!(e?.target?.checked ?? e))}
                  >
                    <span className={block.elem("group-title").toClassName()}>{g.label}</span>
                  </Checkbox>
                  <span className={block.elem("group-hint").toClassName()}>{g.hint}</span>
                </header>
                <div className={block.elem("items").toClassName()}>
                  {visibleItems.length === 0 ? (
                    <span className={block.elem("none").toClassName()}>no match</span>
                  ) : (
                    visibleItems.map((i) => (
                      <label key={i.path} className={block.elem("item").toClassName()}>
                        <Checkbox checked={draft.has(i.path)} onChange={() => toggle(i.path)}>
                          <code title={i.path}>{i.key}</code>
                        </Checkbox>
                      </label>
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </Modal>
  );

  function renderFooter() {
    return (
      <div className={block.elem("foot").mod({ phone }).toClassName()}>
        <span className={block.elem("count").toClassName()}>
          {draft.size} of {allPaths.length} selected
        </span>
        <span className={block.elem("foot-actions").toClassName()}>
          <Button
            variant="neutral"
            look="outlined"
            size={phone ? "medium" : "small"}
            onClick={() => setDraft(new Set(defaultPaths))}
            disabled={isDefault}
          >
            {phone ? "Reset" : "Reset to default"}
          </Button>
          <Button
            variant="neutral"
            look="outlined"
            size={phone ? "medium" : "small"}
            onClick={() => setDraft(new Set(allPaths))}
            disabled={sameAs(allPaths)}
          >
            {phone ? "Select all" : "Select everything"}
          </Button>
        </span>
        <span className={block.elem("spacer").toClassName()} />
        <span className={block.elem("primary").toClassName()}>
          <Button look="outlined" size={phone ? "medium" : "small"} onClick={onClose}>
            Cancel
          </Button>
          <Button size={phone ? "medium" : "small"} onClick={apply}>
            Apply
          </Button>
        </span>
      </div>
    );
  }
};
